import axios, { AxiosResponse } from 'axios';
import { apiClient } from '../apiClient';
import { User } from '../users';
import { WorkTravelZone } from './workTravelZones';
import { WasteProducer } from './wasteProducers';

export interface Site {
    _id: string;
    codeProC: string;
    name: string;
    stateProC: number;
    conducId?: User;
    assistantConducId?: User;
    address?: string;
    longitude?: number;
    latitude?: number;
    crudStatus: number;
    entityId: string;
    isVisibleInApp?: boolean;
    startDate?: string;
    endDate?: string;
    wasteProducerId?: WasteProducer;
    usageCount?: {
        total: number;
        activities: number;
        dailyTransfers: number;
        dailyTransferRides: number;
        dailyTrucksPlanned: number;
        dailyTrucksRides: number;
        gnrRequests: number;
    };
    updatedAt?: string;
    createdAt?: string;
    type: number;
    workTravelZoneId: WorkTravelZone;
}

export interface MonthlySite {
    site: Site;
    dailySiteByDate: {
        date: string;
        users: User[];
    }[];
}

export interface GetMonthlySiteParams {
    date: string;
}

export interface MonthlySitesResponse {
    data: MonthlySite[];
    message: string;
}

export const getMonthlySites = async (params: GetMonthlySiteParams): Promise<MonthlySitesResponse> => {
    try {
        const response: AxiosResponse<MonthlySitesResponse> = await apiClient.get('/sites/monthly/' + params.date);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface SiteExpense {
    siteId: string;
    siteName: string;
    siteCode: string;
    siteColor?: string;
    siteConducName?: string;
    siteConducUsername?: string;
    totalPrice: number;
}

export interface GetSiteExpensesParams {
    startDate: string;
    endDate: string;
}

export interface SiteExpensesResponse {
    message: string;
    data: {
        sites: SiteExpense[];
        totalExpenses: number;
    };
}

export const getSiteExpensesByDateRange = async (queries: GetSiteExpensesParams): Promise<SiteExpensesResponse> => {
    try {
        const response: AxiosResponse<SiteExpensesResponse> = await apiClient.get('/sites/expenses', {
            params: queries,
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

interface SitesResponse {
    message: string;
    data: Site[];
}

export interface GetSitesQueries {
    active?: boolean;
    withUsage?: boolean;
    conducId?: string;
    assistantConducId?: string;
}

export const getAllSites = async (queries?: GetSitesQueries): Promise<SitesResponse> => {
    try {
        const response: AxiosResponse<SitesResponse> = await apiClient.get('/sites', { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface UpdateSiteBody {
    name?: string;
    address?: string | null;
    codeProC?: string;
    conducId?: string | null;
    assistantConducId?: string | null;
    entityId?: string;
    startTime?: string;
    crudStatus?: number;
    isVisibleInApp?: boolean;
    type?: number;
    startDate?: string | null;
    endDate?: string | null;
    workTravelZoneId?: string;
    longitude?: number | null;
    latitude?: number | null;
    wasteProducerId?: string | null;
}

export interface SiteResponse {
    message: string;
    data: Site;
}

export const updateSite = async (siteId: string, data: UpdateSiteBody): Promise<SiteResponse> => {
    try {
        const response: AxiosResponse<SiteResponse> = await apiClient.patch('/sites/' + siteId, data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface CreateSiteBody {
    name: string;
    address?: string | null;
    codeProC: string;
    conducId?: string | null;
    assistantConducId?: string | null;
    entityId: string;
    startTime: string;
    isVisibleInApp?: boolean;
    type: number;
    crudStatus?: number;
    startDate?: string;
    endDate?: string;
    workTravelZoneId: string;
    longitude?: number;
    latitude?: number;
    wasteProducerId?: string;
}

export const createSite = async (data: CreateSiteBody): Promise<SiteResponse> => {
    try {
        const response: AxiosResponse<SiteResponse> = await apiClient.post('/sites', data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};
